/**
 * What the keyboard is allowed to do to the selected row — §15.1.
 *
 * The queue owns the *selection*; the draft pane owns the *actions*. A keymap
 * living in `Workspace` therefore has to reach handlers that are declared three
 * components away, next to the buttons they belong to.
 *
 * The registry exists so the components that render the buttons stay the single
 * source of truth for what those buttons do. Deriving the same thing a second
 * time in `Workspace` — "queue + email means Approve is available" — would be
 * exactly the parallel hand-maintained list that `AREAS` in `App.tsx` was
 * written to avoid, and it would drift the first time a branch changed.
 *
 * Two channels, deliberately:
 *
 *   - the CALLBACKS live in a ref, so a pane re-registering on every keystroke
 *     costs nothing and the keymap always fires the freshest closure;
 *   - the HINT LABELS live in state, because the footer has to repaint when the
 *     available set changes. State updates are gated on a signature string, so
 *     an unmemoised caller cannot loop us.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';

export interface QueueAction {
  /**
   * How the footer names it. Match the button's own word — a hint that says
   * something the visible control does not is worse than no hint.
   */
  label: string;
  run: () => void;
}

/**
 * The fixed set of slots a pane may fill. Slots rather than free-form keys so
 * the key bindings stay in one place and cannot be redefined per pane.
 */
export interface QueueActions {
  /** Space / Enter. The pinned primary — Approve, where there is one. */
  primary?: QueueAction;
  /** E */
  edit?: QueueAction;
  /** R */
  regenerate?: QueueAction;
  /** S */
  snooze?: QueueAction;
  /** X */
  skip?: QueueAction;
}

export type QueueSlot = keyof QueueActions;

/** Slot → key, in the order the footer lists them. */
export const SLOT_KEYS: ReadonlyArray<{ slot: QueueSlot; keyLabel: string }> = [
  { slot: 'primary', keyLabel: 'Space' },
  { slot: 'edit', keyLabel: 'E' },
  { slot: 'regenerate', keyLabel: 'R' },
  { slot: 'snooze', keyLabel: 'S' },
  { slot: 'skip', keyLabel: 'X' },
];

export interface QueueHint {
  keyLabel: string;
  label: string;
}

interface Registry {
  ref: RefObject<QueueActions>;
  hints: QueueHint[];
  publish: (actions: QueueActions) => void;
}

const QueueActionsContext = createContext<Registry | null>(null);

const signatureOf = (hints: QueueHint[]): string =>
  hints.map((h) => `${h.keyLabel}:${h.label}`).join('|');

export function QueueActionsProvider({ children }: { children: ReactNode }) {
  const ref = useRef<QueueActions>({});
  const [hints, setHints] = useState<QueueHint[]>([]);
  /** The last published signature — the loop guard. */
  const signature = useRef('');

  const publish = useCallback((actions: QueueActions) => {
    // Callbacks first, unconditionally: the keymap must never fire a stale one.
    ref.current = actions;

    const next: QueueHint[] = [];
    for (const { slot, keyLabel } of SLOT_KEYS) {
      const action = actions[slot];
      if (action) next.push({ keyLabel, label: action.label });
    }

    const sig = signatureOf(next);
    if (sig === signature.current) return;
    signature.current = sig;
    setHints(next);
  }, []);

  const value = useMemo(() => ({ ref, hints, publish }), [hints, publish]);

  return <QueueActionsContext.Provider value={value}>{children}</QueueActionsContext.Provider>;
}

/** The live callbacks, for the keymap. Reading this never triggers a render. */
export function useQueueActionsRef(): RefObject<QueueActions> {
  const registry = useContext(QueueActionsContext);
  const fallback = useRef<QueueActions>({});
  return registry?.ref ?? fallback;
}

/** What the footer should offer right now. */
export function useQueueHints(): QueueHint[] {
  return useContext(QueueActionsContext)?.hints ?? [];
}

/**
 * Called by whichever pane is rendering the buttons.
 *
 * Exactly one pane is mounted per selected task — `Detail` picks Draft, ReadView
 * or one of the manual views — so registrations do not compete. The cleanup
 * clears the set, which is what makes an unmounted pane's shortcuts stop
 * working the moment its buttons leave the screen.
 *
 * `actions` does not need to be memoised: publishing is idempotent and state
 * only moves when the hint signature changes.
 */
export function useRegisterQueueActions(actions: QueueActions): void {
  const registry = useContext(QueueActionsContext);
  const publish = registry?.publish;

  /*
   * Publish on every render, and clear ONLY on unmount — two effects, because
   * one cannot do both.
   *
   * A single no-deps effect returning `() => publish({})` looks tidier and
   * spins forever: the cleanup runs before every re-run, so each render clears
   * the set (signature changes, state updates, render) and then republishes it
   * (signature changes back, state updates, render). The signature guard cannot
   * catch that — the cleanup is what moves the signature.
   */
  useEffect(() => {
    publish?.(actions);
  });

  useEffect(() => {
    if (!publish) return;
    return () => publish({});
  }, [publish]);
}
